import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
    Stack
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Group as GroupIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getISOWeek, setWeek, setYear, format, subWeeks, startOfWeek } from 'date-fns';
import { initializeDB } from '../../config/db';
import { WEEK_LIST_STORE, EMPLOYEE_STORE, TEMPS_STORE } from '../../config/constants';

export default function SelectFromWeeklyRosterModal({ open, onClose, onSelect, alreadySelected = [] }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [rosters, setRosters] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [temps, setTemps] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);

    useEffect(() => {
        if (open) {
            loadRecentRosters();
            loadEmployeesAndTemps();
        }
    }, [open]);

    async function loadEmployeesAndTemps() {
        try {
            const db = await initializeDB();
            const txE = db.transaction(EMPLOYEE_STORE, 'readonly');
            const txT = db.transaction(TEMPS_STORE, 'readonly');
            const eStore = txE.objectStore(EMPLOYEE_STORE);
            const tStore = txT.objectStore(TEMPS_STORE);
            const [allEmployees, allTemps] = await Promise.all([eStore.getAll(), tStore.getAll()]);
            setEmployees(allEmployees);
            setTemps(allTemps);
        } catch (error) {
            console.error('Error loading employees/temps:', error);
        }
    }

    async function loadRecentRosters() {
        setLoading(true);
        try {
            // Get current user ID from localStorage
            console.log('Checking localStorage for user data...');
            console.log('All localStorage keys:', Object.keys(localStorage));
            const userData = localStorage.getItem('user');
            console.log('Raw user data from localStorage:', userData);
            
            let userId = null;
            
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    console.log('Parsed user object:', user);
                    userId = user.id || user.userId || user.employeeId || user.ID || user.EmployeeID;
                    console.log('Extracted userId:', userId);
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }
            
            // Try alternative storage keys
            if (!userId) {
                const altUser = localStorage.getItem('currentUser') || localStorage.getItem('authUser');
                if (altUser) {
                    try {
                        const user = JSON.parse(altUser);
                        userId = user.id || user.userId || user.employeeId || user.ID || user.EmployeeID;
                        console.log('Found user ID from alternative key:', userId);
                    } catch (e) {
                        console.error('Error parsing alternative user data:', e);
                    }
                }
            }
            
            // Если нет userId - пытаемся получить из JWT token
            if (!userId) {
                const token = localStorage.getItem('token');
                if (token) {
                    try {
                        // Декодируем JWT token
                        const tokenParts = token.split('.');
                        if (tokenParts.length === 3) {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            userId = payload.id || payload.sub || payload.userId;
                            console.log('Found user ID from JWT token:', userId);
                        }
                    } catch (e) {
                        console.error('Error decoding JWT token:', e);
                    }
                }
            }
            
            // Временно разрешаем загрузку всех ростеров если нет userId
            if (!userId) {
                console.warn('No user ID found, will show all rosters without user info');
            }
            
            const db = await initializeDB();
            const tx = db.transaction(WEEK_LIST_STORE, 'readonly');
            const store = tx.objectStore(WEEK_LIST_STORE);
            
            // Get all rosters from IndexedDB
            const allRosters = await store.getAll();
            console.log('All rosters from IndexedDB:', allRosters);
            console.log('Current userId:', userId);
            
            // Get current week number
            const currentWeekNumber = getISOWeek(new Date());
            const currentYear = new Date().getFullYear();
            const currentWeekKey = `${currentYear}-W${String(currentWeekNumber).padStart(2, '0')}`;

            console.log('Current week info:', { currentWeekNumber, currentYear, currentWeekKey });

            // Filter and format rosters for current user
            const formattedRosters = allRosters
                .filter(roster => {
                    // If no userId, show all rosters (for testing)
                    if (!userId) {
                        console.log(`Roster ${roster.weekYear}: showing all rosters (no user filter)`);
                        return true;
                    }

                    // Check if roster belongs to current user
                    // Convert to strings for comparison to handle type mismatches
                    const rosterUserId = String(roster.userId || '');
                    const rosterCreatedBy = String(roster.createdBy || '');
                    const rosterSupervisorId = String(roster.supervisorId || '');
                    const currentUserId = String(userId || '');

                    const belongsToUser = (rosterUserId === currentUserId && rosterUserId !== '') ||
                           (rosterCreatedBy === currentUserId && rosterCreatedBy !== '') ||
                           (rosterSupervisorId === currentUserId && rosterSupervisorId !== '') ||
                           (!roster.userId && !roster.createdBy && !roster.supervisorId); // Include rosters without user info for backward compatibility

                    console.log(`Roster ${roster.weekYear}: userId=${roster.userId}, createdBy=${roster.createdBy}, supervisorId=${roster.supervisorId}, belongsToUser=${belongsToUser}`);
                    return belongsToUser;
                })
                .map(roster => {
                    // Parse week and year from the key
                    let weekNumber = 0;
                    let year = new Date().getFullYear();
                    
                    if (roster.weekYear) {
                        // Handle ISO format: "2024-W45"
                        if (roster.weekYear.includes('-W')) {
                            const parts = roster.weekYear.split('-W');
                            year = parseInt(parts[0]) || year;
                            weekNumber = parseInt(parts[1]) || 0;
                        } 
                        // Handle old format: "45-2024"
                        else {
                            const [weekStr, yearStr] = roster.weekYear.split('-');
                            weekNumber = parseInt(weekStr) || 0;
                            year = parseInt(yearStr) || year;
                        }
                    } else if (roster.id) {
                        // Fallback to id field
                        const [weekStr, yearStr] = roster.id.split('-');
                        weekNumber = parseInt(weekStr) || 0;
                        year = parseInt(yearStr) || year;
                    }
                    
                    // Use values from roster if available
                    if (roster.weekNumber) weekNumber = roster.weekNumber;
                    if (roster.year) year = roster.year;
                    
                    // Create date from week and year
                    const date = new Date(year, 0, 1);
                    date.setDate(date.getDate() + (weekNumber - 1) * 7);
                    
                    return {
                        weekYear: roster.weekYear || roster.id || `${weekNumber}-${year}`,
                        weekNumber,
                        year,
                        date,
                        employees: roster.employees || [],
                        permanentEmployees: roster.permanentEmployees || [],
                        temporaryEmployees: roster.temporaryEmployees || [],
                        userId: roster.userId || roster.createdBy || roster.supervisorId,
                        ...roster
                    };
                })
                .filter(roster => {
                    // Filter only current week roster
                    if (roster.weekNumber <= 0) return false;

                    // Check if this is the current week
                    const isCurrentWeek = (roster.weekYear === currentWeekKey) ||
                                         (roster.weekNumber === currentWeekNumber && roster.year === currentYear);

                    console.log(`Roster ${roster.weekYear}: weekNumber=${roster.weekNumber}, year=${roster.year}, isCurrentWeek=${isCurrentWeek}`);
                    return isCurrentWeek;
                })
                .sort((a, b) => b.date - a.date); // Sort by date, newest first
            
            console.log('Formatted rosters after filtering:', formattedRosters);
            console.log('Total rosters found:', formattedRosters.length);
            setRosters(formattedRosters);
        } catch (error) {
            console.error('Error loading rosters from IndexedDB:', error);
            setRosters([]);
        } finally {
            setLoading(false);
        }
    }

    function handleSelectWeek(roster) {
        setSelectedWeek(roster);
    }

    function handleConfirm() {
        if (!selectedWeek) return;

        console.log('Selected week data:', selectedWeek);
        
        // Get all employee IDs from the selected roster
        let employeeIds = [];
        
        // Handle both old and new format
        if (selectedWeek.permanentEmployees?.length || selectedWeek.temporaryEmployees?.length) {
            employeeIds = [
                ...(selectedWeek.permanentEmployees || []),
                ...(selectedWeek.temporaryEmployees || [])
            ];
            console.log('Using permanentEmployees + temporaryEmployees:', employeeIds);
        } else {
            employeeIds = selectedWeek.employees || [];
            console.log('Using employees array:', employeeIds);
        }

        // Map IDs to full employee objects
        const selectedEmployees = [];
        
        console.log('Processing employee IDs:', employeeIds);
        console.log('Available employees:', employees.length);
        console.log('Available temps:', temps.length);
        
        if (employeeIds.length === 0) {
            console.warn('No employee IDs found in selected roster');
            onSelect([]);
            handleClose();
            return;
        }
        
        employeeIds.forEach(id => {
            console.log(`Processing ID: ${id} (type: ${typeof id})`);
            
            // Check if it's a permanent employee
            const employee = employees.find(e => {
                const matches = (e.id == id) ||
                    (e.EmployeeID == id) || 
                    (e.employeeID == id) ||
                    (e.ID == id) ||
                    (e.FullName === id); // Sometimes ID might be the name
                
                if (matches) {
                    console.log(`Match found for employee:`, e);
                }
                return matches;
            });
            
            if (employee) {
                console.log('Found employee:', employee);
                selectedEmployees.push({
                    // id/EE/Phone обязательны: без них потребители (тренинг-лист,
                    // таймшит, LTT) не могут ни сопоставить человека, ни взять номер.
                    id: employee.id ?? employee.ID ?? employee.EmployeeID ?? employee.employeeID ?? null,
                    EE: employee.EE ?? employee.ee ?? null,
                    Phone: employee.Phone || employee.phone || '',
                    FullName: employee.FullName || employee.fullName || employee.Name || 'Unknown',
                    Division: employee.Division || employee.division || employee.DivisionCode || 'No Division',
                    _type: 'employee'
                });
                return;
            }
            
            // Check if it's a temp
            const temp = temps.find(t => {
                const matches = (t.id == id) ||
                    (t.EmployeeID == id) || 
                    (t.employeeID == id) ||
                    (t.ID == id) ||
                    (t.FullName === id);
                
                if (matches) {
                    console.log(`Match found for temp:`, t);
                }
                return matches;
            });
            
            if (temp) {
                console.log('Found temp:', temp);
                selectedEmployees.push({
                    id: temp.id ?? temp.ID ?? temp.EmployeeID ?? temp.employeeID ?? null,
                    Phone: temp.Phone || temp.phone || '',
                    FullName: temp.FullName || temp.fullName || temp.Name || 'Unknown',
                    Agency: temp.Agency || temp.agency || 'No Agency',
                    Division: String(temp.Division ?? temp.division ?? '').trim(),
                    _type: 'temp'
                });
            } else {
                console.log('Employee/Temp not found for ID:', id);
            }
        });

        // Filter out already selected employees
        const filteredEmployees = selectedEmployees.filter(emp => {
            const isDuplicate = alreadySelected.some(selected =>
                selected.FullName === emp.FullName && selected._type === emp._type
            );
            return !isDuplicate;
        });

        console.log('Selected employees after filtering duplicates:', filteredEmployees);
        onSelect(filteredEmployees);
        handleClose();
    }

    function handleClose() {
        setSelectedWeek(null);
        onClose();
    }

    function getRosterEmployeeCount(roster) {
        if (roster.permanentEmployees?.length || roster.temporaryEmployees?.length) {
            const permCount = roster.permanentEmployees?.length || 0;
            const tempCount = roster.temporaryEmployees?.length || 0;
            return {
                total: permCount + tempCount,
                permanent: permCount,
                temporary: tempCount
            };
        }
        return {
            total: roster.employees?.length || 0,
            permanent: 0,
            temporary: 0
        };
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Select from Weekly Roster</Typography>
                    <IconButton onClick={handleClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : rosters.length === 0 ? (
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body1" fontWeight="600" gutterBottom>
                                No weekly roster for current week
                            </Typography>
                            <Typography variant="body2">
                                You don't have a weekly roster for the current week. Please create one to continue.
                            </Typography>
                        </Alert>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    handleClose();
                                    navigate('/admin/weekly-employee');
                                }}
                            >
                                Create Weekly Roster
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <List>
                        {rosters.map((roster) => {
                            const counts = getRosterEmployeeCount(roster);
                            const isSelected = selectedWeek?.weekYear === roster.weekYear;
                            const weekStart = startOfWeek(roster.date);
                            
                            return (
                                <ListItem 
                                    key={roster.weekYear}
                                    disablePadding
                                    sx={{ mb: 1 }}
                                >
                                    <ListItemButton
                                        onClick={() => handleSelectWeek(roster)}
                                        selected={isSelected}
                                        sx={{
                                            borderRadius: 1,
                                            border: '2px solid',
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.08)' : 'white',
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(33, 150, 243, 0.12)'
                                                }
                                            },
                                            '&:hover': {
                                                backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <ListItemIcon>
                                            {isSelected ? (
                                                <CheckCircleIcon color="primary" />
                                            ) : (
                                                <CalendarIcon />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="600">
                                                        Week {roster.weekNumber}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {format(weekStart, 'MMM d, yyyy')}
                                                        {roster.supervisorName && (
                                                            <> • Created by: {roster.supervisorName}</>
                                                        )}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                        <Chip
                                                            icon={<GroupIcon />}
                                                            label={`${counts.total} Total`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        {counts.permanent > 0 && (
                                                            <Chip
                                                                label={`${counts.permanent} Employees`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#1976d2',
                                                                    color: 'white',
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                        )}
                                                        {counts.temporary > 0 && (
                                                            <Chip
                                                                label={`${counts.temporary} Temps`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#f57c00',
                                                                    color: 'white',
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleConfirm}
                    disabled={!selectedWeek}
                    startIcon={<CheckCircleIcon />}
                >
                    Load Selected Roster
                </Button>
            </DialogActions>
        </Dialog>
    );
}